import { useEffect, useId, useMemo, useState } from "react";
import {
    Badge,
    Button,
    Toast,
    Toaster,
    useToastController,
    makeStyles,
    tokens,
    Tooltip,
    ToastTitle,
    Spinner
} from "@fluentui/react-components";
import {
    Delete16Regular,
    ArrowClockwise16Regular,
    LinkEdit16Regular,
    Info16Regular,
} from "@fluentui/react-icons";
import { formatDateTime } from "../adminUtil";
import SalesforceConnectButton from "../SalesforceConnection/SalesforceConnectButton";
import AdminService from "../AdminService";
import SalesforceMoreMenu from "../SalesforceConnection/SalesforceMoreMenu";
import ConfirmationModal from "../ConfirmationModal";
import GlobalTableSearch from "../ManageCustomers/GlobalTableSearch";
import CliLookupObjectsDialog from "../SalesforceConnection/CliLookupObjectsDialog";

const useStyles = makeStyles({
    wrapper: {
        backgroundColor: tokens.colorNeutralBackground2,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusMedium,
        padding: "8px",
    },
    header: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        marginBottom: "8px",
        justifyContent: "space-between",
    },
    badge: {
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "999px",
        fontSize: "12px",
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForegroundLink,
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        backgroundColor: tokens.colorNeutralBackground1,

        "& thead th": {
            background: tokens.colorNeutralBackground2,
            fontWeight: 600,
            fontSize: "13px",
        },

        "& th, & td": {
            padding: "4px 6px",
            borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
            textAlign: "left",
            verticalAlign: "middle",
            fontSize: "13px",
        },

        "& tbody tr:hover td": {
            backgroundColor: tokens.colorSubtleBackgroundHover,
        },
    },
    tableHeader: {
        position: "sticky",
        top: 0,
        zIndex: 2,
        background: tokens.colorNeutralBackground2,
    },
    actions: {
        "& > :not(:last-child)": {
            marginRight: "6px",
        },
    },
    createdHeader: {
        width: "150px",
    },
    instanceUrlCell: {
        width: "240px"
    },
    empty: {
        color: tokens.colorNeutralForeground3,
        padding: "10px",
    },
    usernameCell: {
        maxWidth: "230px",
        whiteSpace: "normal",
        wordBreak: "break-word",
    },
    actionsDiv: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-evenly"
    }
});

function toArraySf(sf) {
    if (!sf) return [];
    return Array.isArray(sf) ? sf : [];
}

// Chip color per CLI lookup object type
const CLI_CHIP_COLORS = {
    Lead: "informative",
    Contact: "success",
    Account: "warning",
};

export default function SfOrgsTable({ tenant, onChange }) {
    // ── All hooks must be declared before any early return ──────────────────
    const styles = useStyles();
    const [rows, setRows] = useState(() =>
        toArraySf(tenant?.config?.sf)
    );
    const [deletingOrgId, setDeletingOrgId] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [search, setSearch] = useState("");
    // { id: tenantSfOrgId (UUID), orgId: sfOrgId (public ID) } or null
    const [cliDialogTarget, setCliDialogTarget] = useState(null);
    // { [orgId]: true } — in-flight CLI lookup saves (spinner in the cell). Absent = idle.
    const [cliSavingOrgs, setCliSavingOrgs] = useState({});

    useEffect(() => {
        setRows(toArraySf(tenant?.config?.sf));
    }, [tenant?.config?.sf]);

    const toasterId = useId();
    const { dispatchToast } = useToastController(toasterId);

    const cliDialogRow = useMemo(
        () => rows.find((r) => r.id === cliDialogTarget?.id) ?? null,
        [rows, cliDialogTarget],
    );

    // Client-side filtered rows
    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(
            (r) =>
                (r.orgId ?? "").toLowerCase().includes(q) ||
                (r.instanceUrl ?? "").toLowerCase().includes(q) ||
                (r.sfOrgOwner ?? "").toLowerCase().includes(q) ||
                (r.sfOrgOwnerUserId ?? "").toLowerCase().includes(q) ||
                (Array.isArray(r.cliLookupObjects)
                    ? r.cliLookupObjects.some((o) => o.toLowerCase().includes(q))
                    : false),
        );
    }, [rows, search]);

    // Guard after hooks — tenant prop may be null while parent is still loading
    if (!tenant || !tenant.config) return null;

    const deleteOrg = async (orgId) => {
        const prevRows = [...rows]; // backup for rollback

        //  optimistic update
        const next = rows.filter((r) => r.orgId !== orgId);
        setRows(next);

        onChange({
            ...tenant,
            config: {
                ...tenant.config,
                sf: next,
            },
        });

        setDeletingOrgId(orgId);
        setDeleteTarget(null);

        try {
            await AdminService.deleteSFConnection(orgId);

            dispatchToast(
                <Toast>
                    <ToastTitle>Deleted {orgId} successfully ✅</ToastTitle>
                </Toast>,
                { intent: "success" }
            );
        } catch (err) {
            console.error("Delete failed", err);

            //  rollback UI
            setRows(prevRows);

            onChange({
                ...tenant,
                config: {
                    ...tenant.config,
                    sf: prevRows,
                },
            });

            dispatchToast(
                <Toast>
                    <ToastTitle>Failed to delete org ❌</ToastTitle>
                </Toast>,
                { intent: "error" }
            );
        } finally {
            setDeletingOrgId(null);
        }
    };
    const handleToggleScv = async (enabled) => {
        console.log("SCV Enabled:", enabled);

        // API CALL
        // await AdminService.updateScvStatus(enabled);
    };

    const handleToggleSync = async (enabled) => {
        console.log("Sync Enabled:", enabled);

        // API CALL
        // await AdminService.updateSyncStatus(enabled);
    };

    const generateCredentialsFor = (sfOrgId) => async () => {
        try {
            return await AdminService.generateScvCredentials(sfOrgId);
        } catch (err) {
            console.error("generateScvCredentials failed", err);
            dispatchToast(
                <Toast>
                    <ToastTitle>
                        {err?.error || "Failed to generate credentials"} ❌
                    </ToastTitle>
                </Toast>,
                { intent: "error" }
            );
            // Re-throw so the dialog stays in its pre-generated state.
            throw err;
        }
    };

    // Update a single row's Caller Number Lookup Objects locally (UI-only)
    const handleSaveCliLookupObjects = (orgId, objectIds) => {
        setRows((prev) =>
            prev.map((r) =>
                r.orgId === orgId ? { ...r, cliLookupObjects: objectIds } : r,
            ),
        );
    };

    return (
        <div className={styles.wrapper}>
            <Toaster toasterId={toasterId} />

            {/* Caller Number Lookup Objects dialog — one instance, driven by cliDialogTarget */}
            <CliLookupObjectsDialog
                open={!!cliDialogTarget}
                selectedObjects={cliDialogRow?.cliLookupObjects ?? []}
                onSave={async (ids) => {
                    // Guard: target could theoretically be null in a race
                    if (!cliDialogTarget) return;
                    const { id: tenantSfOrgId, orgId } = cliDialogTarget;
                    // Capture the previous value NOW, before setCliDialogTarget(null)
                    // makes cliDialogRow go null and the rollback loses the real data.
                    const prevObjects = cliDialogRow?.cliLookupObjects ?? null;
                    // Optimistic local update so the chips refresh immediately.
                    // Use [] (not null) for cleared state to match what the server will return next fetch.
                    handleSaveCliLookupObjects(orgId, ids);
                    setCliDialogTarget(null);
                    // Mark this org as saving — the cell will show a Spinner in place of the chips.
                    setCliSavingOrgs((prev) => ({ ...prev, [orgId]: true }));
                    try {
                        await AdminService.updateCliLookupObjects({
                            tenantSfOrgId,
                            objectIds: ids,
                        });
                        dispatchToast(
                            <Toast>
                                <ToastTitle>
                                    Caller Number Lookup Objects saved successfully ✅
                                </ToastTitle>
                            </Toast>,
                            { intent: "success" },
                        );
                    } catch (err) {
                        console.error("Failed to save Caller Number Lookup Objects", err);
                        dispatchToast(
                            <Toast>
                                <ToastTitle>
                                    {err?.message || "Failed to save Caller Number Lookup Objects"} ❌
                                </ToastTitle>
                            </Toast>,
                            { intent: "error" },
                        );
                        // Roll back to the actual previous value (prevObjects, not cliDialogRow which is now null)
                        handleSaveCliLookupObjects(orgId, prevObjects);
                    } finally {
                        setCliSavingOrgs((prev) => {
                            if (!prev[orgId]) return prev;
                            const next = { ...prev };
                            delete next[orgId];
                            return next;
                        });
                    }
                }}
                onCancel={() => setCliDialogTarget(null)}
            />

            <ConfirmationModal
                isOpen={!!deleteTarget}
                type={deleteTarget?.linkedQueueCount > 0 ? "warning" : "delete"}
                title="Delete Salesforce Org"
                message={
                    deleteTarget?.linkedQueueCount > 0
                        ? `"${deleteTarget?.orgId}" org is currently linked to ${deleteTarget.linkedQueueCount} queue${deleteTarget.linkedQueueCount === 1 ? "" : "s"}. Please unlink all associated queues in Manage Queue before deleting this org.`
                        : `Are you sure you want to delete org "${deleteTarget?.orgId}"? This action cannot be undone.`
                }
                confirmText="Delete"
                confirmDisabled={deleteTarget?.linkedQueueCount > 0}
                onConfirm={() => deleteOrg(deleteTarget?.orgId)}
                onCancel={() => setDeleteTarget(null)}
                loading={deletingOrgId === deleteTarget?.orgId}
            />

            <div className={styles.header}>
                <div>
                    <strong>Salesforce Orgs</strong>
                    <span className={styles.badge}>
                        {filteredRows.length}{search ? ` of ${rows.length}` : ""} org(s)
                    </span>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <GlobalTableSearch
                        value={search}
                        onChange={setSearch}
                        placeholder="Search orgs..."
                        debounceMs={0}
                    />
                    <SalesforceConnectButton />
                </div>
            </div>

            <table className={styles.table}>
                <thead className={styles.tableHeader}>
                    <tr>
                        <th>Org ID</th>
                        <th className={styles.instanceUrlCell}>Instance URL</th>
                        <th style={{ width: 110 }}>User ID</th>
                        <th style={{ width: 200 }}>Username</th>
                        <th style={{ width: 140 }}>Status</th>
                        <th style={{ width: 190, whiteSpace: "normal", lineHeight: 1.25 }}>
                            Caller Number Lookup Objects
                            {"\u00A0"}
                            <Tooltip
                                content="This allows you to select what objects to lookup the inbound caller number against, choosing to match this against a Contact, Lead, and Account records."
                                relationship="description"
                                positioning="above"
                                withArrow
                            >
                                <Button
                                    appearance="transparent"
                                    size="small"
                                    icon={<Info16Regular />}
                                    aria-label="Caller Number Lookup Objects information"
                                    style={{
                                        minWidth: "auto",
                                        padding: 0,
                                        height: "16px",
                                        color: "#616161",
                                        cursor: "help",
                                        verticalAlign: "text-bottom",
                                    }}
                                />
                            </Tooltip>
                        </th>
                        <th className={styles.createdHeader}>Created At (UTC)</th>
                        <th className={styles.createdHeader}>Updated At (UTC)</th>
                        <th style={{ width: 110 }}>Actions</th>
                    </tr>
                </thead>

                <tbody>
                    {filteredRows.map((r) => (
                        <tr key={r.orgId}>
                            <td>{r.orgId}</td>
                            <td>{r.instanceUrl || "-"}</td>
                            <td>{r.sfOrgOwnerUserId || "-"}</td>
                            <td className={styles.usernameCell}>
                                {r.sfOrgOwner || "-"}
                            </td>
                            <td>
                                <div style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 4 }}>
                                    {r.isSyncAuthenticated ? (
                                        <Badge appearance="filled" color="success">Sync</Badge>
                                    ) : r.isSyncEnabled ? (
                                        <Badge appearance="outline" color="warning">Sync pending</Badge>
                                    ) : null}
                                    {r.isScvAuthenticated ? (
                                        <Badge appearance="filled" color="success">SCV</Badge>
                                    ) : r.isScvEnabled ? (
                                        <Badge appearance="outline" color="warning">SCV pending</Badge>
                                    ) : null}
                                    {!r.isSyncEnabled && !r.isScvEnabled && (
                                        <Badge appearance="ghost">Not linked</Badge>
                                    )}
                                </div>
                            </td>
                            <td>
                                {(() => {
                                    // While the CLI save API is in flight, show a Spinner in place of chips.
                                    if (cliSavingOrgs[r.orgId]) {
                                        return (
                                            <div style={{ display: "inline-flex", alignItems: "center" }}>
                                                <Spinner size="tiny" aria-label="Saving Caller Number Lookup Objects" />
                                            </div>
                                        );
                                    }
                                    // Backend always returns an array: [] = explicitly cleared, [...] = configured
                                    const display = r.cliLookupObjects || [];
                                    return display.length === 0 ? (
                                        <span style={{ fontSize: "12px", fontStyle: "italic", color: tokens.colorNeutralForeground3 }}>
                                            None configured
                                        </span>
                                    ) : (
                                        <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", width: "80%" }}>
                                            {display.map((obj) => (
                                                <Badge
                                                    key={obj}
                                                    appearance="tint"
                                                    color={CLI_CHIP_COLORS[obj] || "brand"}
                                                    shape="rounded"
                                                    size="small"
                                                >
                                                    {obj}
                                                </Badge>
                                            ))}
                                        </div>
                                    );
                                })()}
                            </td>
                            <td>
                                {formatDateTime(r.createdAt)}
                            </td>
                            <td>
                                {formatDateTime(r.updatedAt)}
                            </td>

                            <td className={styles.actions}>
                                <div className={styles.actionsDiv}>
                                    <SalesforceConnectButton>
                                        <Tooltip content="Refresh Connection">
                                            <Button
                                                size="small"
                                                appearance="transparent"
                                                icon={<ArrowClockwise16Regular />}
                                            />
                                        </Tooltip>
                                    </SalesforceConnectButton>

                                    <Tooltip content="Configure Caller Number Lookup Objects" relationship="label">
                                        <Button
                                            size="small"
                                            appearance="transparent"
                                            icon={<LinkEdit16Regular />}
                                            onClick={() => setCliDialogTarget({ id: r.id, orgId: r.orgId })}
                                            aria-label="Configure Caller Number Lookup Objects"
                                            disabled={!!cliSavingOrgs[r.orgId]}
                                        />
                                    </Tooltip>

                                    <Tooltip content="Delete">
                                        <Button
                                            size="small"
                                            appearance="transparent"
                                            icon={
                                                deletingOrgId === r.orgId ? <Spinner size="tiny" /> : <Delete16Regular />
                                            }
                                            onClick={() => setDeleteTarget(r)}
                                            disabled={deletingOrgId === r.orgId}
                                        />
                                    </Tooltip>
                                    <SalesforceMoreMenu
                                        scvEnabled={Boolean(r.isScvEnabled)}
                                        syncEnabled={Boolean(r.isSyncEnabled)}
                                        onToggleScv={handleToggleScv}
                                        onToggleSync={handleToggleSync}
                                        onGenerateCredentials={generateCredentialsFor(r.orgId)}
                                    />
                                </div>
                            </td>
                        </tr>
                    ))}

                    {filteredRows.length === 0 && (
                        <tr>
                            <td className={styles.empty} colSpan={9}>
                                {search ? `No orgs match "${search}".` : "No Salesforce orgs yet."}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}